import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type {
  ClienteCreate,
  ClienteUpdate,
  ContactoCreate,
  ContactoUpdate,
  FormaPago,
  RegimenFiscal,
  UsoCfdi,
} from "@uirtus/shared";
import { api } from "./api";

export interface Contacto {
  id: string;
  clienteId: string;
  nombre: string;
  puesto: string | null;
  email: string | null;
  telefono: string | null;
  esPrincipal: boolean;
}

export interface Cliente {
  id: string;
  rfc: string;
  razonSocial: string;
  nombreComercial: string | null;
  regimenFiscal: RegimenFiscal;
  codigoPostal: string;
  usoCfdiDefault: UsoCfdi | null;
  formaPagoDefault: FormaPago | null;
  email: string | null;
  telefono: string | null;
  sitioWeb: string | null;
  diasCredito: number;
  notas: string | null;
  activo: boolean;
  enListaNegra: boolean;
  listaNegraRevisadoAt: string | null;
  creadoAt: string;
}

export interface ClienteConDetalle extends Cliente {
  contactos: Contacto[];
  /** Cuántas facturas, proyectos y cotizaciones cuelgan del cliente. */
  uso: { facturas: number; proyectos: number; cotizaciones: number };
}

const CLAVE = ["clientes"] as const;

export function useClientes(filtros: { q?: string; activo?: boolean }) {
  const parametros = new URLSearchParams();
  if (filtros.q) parametros.set("q", filtros.q);
  if (filtros.activo !== undefined) parametros.set("activo", String(filtros.activo));
  const cadena = parametros.toString();

  return useQuery({
    queryKey: [...CLAVE, filtros],
    queryFn: () => api<Cliente[]>(`/clientes${cadena ? `?${cadena}` : ""}`),
  });
}

export function useCliente(id: string) {
  return useQuery({
    queryKey: [...CLAVE, id],
    queryFn: () => api<ClienteConDetalle>(`/clientes/${id}`),
  });
}

/**
 * Invalida la lista y, si aplica, la ficha. Todas las mutaciones pasan por
 * aquí para que no se quede una pantalla mostrando datos viejos.
 */
function useMutacionCliente<TDatos, TVars>(
  fn: (vars: TVars) => Promise<TDatos>,
  opciones?: UseMutationOptions<TDatos, Error, TVars>,
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: fn,
    ...opciones,
    // Los argumentos se reenvían tal cual para no quedar atados al número de
    // parámetros que React Query le pase al callback en cada versión.
    onSuccess: (...args) => {
      void cliente.invalidateQueries({ queryKey: CLAVE });
      opciones?.onSuccess?.(...args);
    },
  });
}

export const useCrearCliente = () =>
  useMutacionCliente((body: ClienteCreate) =>
    api<Cliente>("/clientes", { method: "POST", body }),
  );

export const useActualizarCliente = (id: string) =>
  useMutacionCliente((body: ClienteUpdate) =>
    api<Cliente>(`/clientes/${id}`, { method: "PATCH", body }),
  );

export const useCambiarActivo = (id: string) =>
  useMutacionCliente((activo: boolean) =>
    api<Cliente>(`/clientes/${id}/${activo ? "reactivar" : "desactivar"}`, {
      method: "POST",
    }),
  );

export const useEliminarCliente = (id: string) =>
  useMutacionCliente(() => api<void>(`/clientes/${id}`, { method: "DELETE" }));

export const useRevisarListaNegra = (id: string) =>
  useMutacionCliente(() =>
    api<{ cliente: Cliente; mensaje: string }>(
      `/clientes/${id}/revisar-lista-negra`,
      { method: "POST" },
    ),
  );

export const useCrearContacto = (clienteId: string) =>
  useMutacionCliente((body: ContactoCreate) =>
    api<Contacto>(`/clientes/${clienteId}/contactos`, { method: "POST", body }),
  );

export const useActualizarContacto = () =>
  useMutacionCliente(({ id, ...body }: ContactoUpdate & { id: string }) =>
    api<Contacto>(`/contactos/${id}`, { method: "PATCH", body }),
  );

export const useEliminarContacto = () =>
  useMutacionCliente((id: string) =>
    api<void>(`/contactos/${id}`, { method: "DELETE" }),
  );
